// Surface detail for the world (2026-10-26, graphics pass "more realistic"): the close-up texture of a real tabletop,
// made once at load in code, so it costs no download. Pure (typed arrays only, no three or DOM) so tests can hold it
// to tiling and to the same bytes every load.
//
// One 512 x 512 tile, four channels, each a height or a shade the board's shader reads:
//   R  felt: short crossing fibers over soft pilling (the nap of a felt mat, about 1 cm a texel on the table)
//   G  sand: fine grain with small pebbles standing out of it
//   B  pebble shade: 128 is plain sand, above or below a lighter or darker stone
//   A  broad mottling, tiled slowly (the shader samples it at a large scale to break up the repeat)
// Everything wraps at the tile's edge, so the board can repeat it with no seam.

export const DETAIL = 512;

/** An integer hash to [0, 1), stable across loads (the same mix as board.ts's placement hash). */
export function hash3(i: number, j: number, k = 0) {
  let h = Math.imul(i, 0x27d4eb2d) ^ Math.imul(j, 0x165667b1) ^ Math.imul(k + 1, 0x9e3779b1);
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b); h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/** Value noise that repeats every `cells` lattice cells across the tile, smooth (smoothstep) between corners. */
function periodicNoise(out: Float32Array, cells: number, seed: number, amp: number) {
  const N = DETAIL, step = cells / N, lat = new Float32Array(cells * cells);
  for (let k = 0; k < lat.length; k++) lat[k] = hash3(k % cells, (k / cells) | 0, seed);
  for (let j = 0; j < N; j++) {
    const y = j * step, y0 = Math.floor(y), fy = y - y0, sy = fy * fy * (3 - 2 * fy), r0 = y0 * cells, r1 = ((y0 + 1) % cells) * cells;
    for (let i = 0; i < N; i++) {
      const x = i * step, x0 = Math.floor(x), fx = x - x0, sx = fx * fx * (3 - 2 * fx), x1 = (x0 + 1) % cells;
      const a = lat[r0 + x0], b = lat[r0 + x1], c = lat[r1 + x0], d = lat[r1 + x1];
      out[j * N + i] += amp * (a + (b - a) * sx + (c - a + (a - b + d - c) * sx) * sy);
    }
  }
}

/** Stretches a channel to 0..1 by its own range. */
function normalize(a: Float32Array) {
  let lo = Infinity, hi = -Infinity;
  for (const v of a) { if (v < lo) lo = v; if (v > hi) hi = v; }
  const s = hi > lo ? 1 / (hi - lo) : 0;
  for (let k = 0; k < a.length; k++) a[k] = (a[k] - lo) * s;
}

/** The tile as RGBA bytes (DETAIL x DETAIL x 4). About 60 ms in node on the box. */
export function detailTile(): Uint8Array {
  const N = DETAIL, M = N - 1, felt = new Float32Array(N * N), sand = new Float32Array(N * N), shade = new Float32Array(N * N).fill(0.5), broad = new Float32Array(N * N);
  // felt: pilling at two sizes, then fibers, short strokes at every angle laid over each other
  periodicNoise(felt, 32, 1, 0.5); periodicNoise(felt, 96, 2, 0.35);
  for (let s = 0; s < 26000; s++) {
    const x0 = hash3(s, 1, 11) * N, y0 = hash3(s, 2, 11) * N, a = hash3(s, 3, 11) * Math.PI, len = 3 + hash3(s, 4, 11) * 7;
    const v = (hash3(s, 5, 11) - 0.35) * 0.55, dx = Math.cos(a) * 0.5, dy = Math.sin(a) * 0.5;
    for (let t = 0; t < len * 2; t++) felt[(((y0 + dy * t) | 0) & M) * N + (((x0 + dx * t) | 0) & M)] += v;
  }
  // sand: grain (a texel each, softened by its neighbours), slow ripples, then pebbles as little domes, each its own shade
  const grain = new Float32Array(N * N);
  for (let k = 0; k < grain.length; k++) grain[k] = hash3(k & M, k >> 9, 21);
  for (let j = 0; j < N; j++) for (let i = 0; i < N; i++) {
    sand[j * N + i] = 0.5 * grain[j * N + i] + 0.125 * (grain[j * N + ((i + 1) & M)] + grain[j * N + ((i - 1) & M)] + grain[((j + 1) & M) * N + i] + grain[((j - 1) & M) * N + i]);
  }
  periodicNoise(sand, 16, 3, 0.6);
  for (let p = 0; p < 380; p++) {
    const cx = hash3(p, 1, 31) * N, cy = hash3(p, 2, 31) * N, r = 1.6 + hash3(p, 3, 31) ** 2 * 4.5, e = 0.6 + hash3(p, 4, 31) * 0.5, rot = hash3(p, 5, 31) * Math.PI;
    // mostly pale stones, a few a little darker: never black specks (STYLE.md: never dirty)
    const tone = hash3(p, 6, 31) < 0.25 ? 0.36 + hash3(p, 7, 31) * 0.14 : 0.58 + hash3(p, 7, 31) * 0.3, c = Math.cos(rot), sn = Math.sin(rot);
    for (let y = Math.floor(cy - r - 1); y <= cy + r + 1; y++) for (let x = Math.floor(cx - r - 1); x <= cx + r + 1; x++) {
      const dx = x + 0.5 - cx, dy = y + 0.5 - cy, u = (dx * c + dy * sn) / r, w = (-dx * sn + dy * c) / (r * e), d = u * u + w * w;
      if (d >= 1) continue;
      const k = (y & M) * N + (x & M), dome = Math.sqrt(1 - d);
      sand[k] = Math.max(sand[k], 1.2 + 0.8 * dome);
      shade[k] = tone;
    }
  }
  periodicNoise(broad, 2, 4, 0.6); periodicNoise(broad, 5, 5, 0.4);
  normalize(felt); normalize(sand); normalize(broad);
  const out = new Uint8Array(N * N * 4);
  for (let k = 0; k < N * N; k++) {
    out[k * 4] = Math.round(felt[k] * 255);
    out[k * 4 + 1] = Math.round(sand[k] * 255);
    out[k * 4 + 2] = Math.round(shade[k] * 255);
    out[k * 4 + 3] = Math.round(broad[k] * 255);
  }
  return out;
}
